import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Router } from '@angular/router';
import { translate, translateObject } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import {
  EmbeddingOptionsComponent,
  EmbeddingOptionsComponentOptions,
} from 'app/components/embedding-options/embedding-options.component';
import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { WidgetDriverAnalysisOptions } from 'app/models';
import { UploadCreateDialogComponent } from 'app/modules/upload/components/upload-create/upload-create.component';
import { UploadInfoDialogOptions } from 'app/modules/upload/components/upload-info/upload-info.component';
import { AnalyticsService, TimerFn } from 'app/providers/analytics.service';
import { SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import { Group, GroupService } from 'app/providers/semantria-api/group.service';
import { Analysis, AnalysisService } from 'app/providers/storage-api/analysis.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import {
  CheckEmbeddingsResult,
  InteractiveSmartSummaryService,
} from 'app/providers/storage-api/interactive-smart-summary.service';
import { PredictiveService } from 'app/providers/storage-api/predictive.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { DownloadTasksService, TaskType } from 'app/providers/tasks-api/download.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from '../../../../providers/semantria-api/configuration.service';
import {
  Collection,
  CollectionService,
} from '../../../../providers/storage-api/collection.service';
import { UploadState } from '../../../../providers/storage-api/upload.service';
import { AlertService } from '../../../../services/alert.service';
import { CalculatedFieldConfigModalComponent } from '../calculated-field-config-modal/calculated-field-config-modal.component';
import { CollectionSceneSelectDialogComponent } from '../collection-scene-select-dialog/collection-scene-select-dialog.component';
import { ReAnalyzeModalComponent } from '../reanalyze-modal/reanalyze-modal.component';
import { AutoAnalysisAction, ProjectCollection } from '../../models/project-collection.model';

import { ProjectDetailService, ProjectItem } from '../../services/project-detail.service';

export type WidgetDriverAnalysisCreateOptions = Omit<WidgetDriverAnalysisOptions, 'dataSource'> & {
  analysisId: string;
};

export type AnalysisActionTypes =
  | 'OPEN_RENAME_ANALYSIS_DIALOG'
  | 'OPEN_DELETE_ANALYSIS_DIALOG'
  | 'OPEN_NEW_ANALYSIS_DIALOG'
  | 'OPEN_DELETE_DRIVER_ANALYSIS_DIALOG'
  | 'OPEN_RE_ANALYSIS_DIALOG'
  | 'OPEN_STOP_RE_ANALYSIS_DIALOG'
  | 'CREATE_EMBEDDINGS'
  | 'COPY_ID_TO_CLIPBOARD'
  | 'CHECK_EMBEDDINGS'
  | 'REMOVE_EMBEDDINGS';

@EnableClassTelemetry()
@Component({
  selector: 'app-project-collection-actions',
  imports: [
    AppSharedModule,
    CollectionSceneSelectDialogComponent,
    CalculatedFieldConfigModalComponent,
    UploadCreateDialogComponent,
  ],
  templateUrl: './project-collection-actions.component.html',
  styleUrls: ['./project-collection-actions.component.scss'],
})
export class ProjectCollectionActionsComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() projectCollections: ProjectCollection[];
  @Input() project: Project;
  @Input() configurations: Map<string, SemantriaConfiguration>;
  @Input() groupConfigurations: SemantriaConfiguration[];

  showDriverAnalysisModal: boolean;
  driverAnalysisModalData: any = {};
  showSceneSelectModal: boolean;
  sceneSelectModalData: { collection?: ProjectCollection } = {};
  showRenameCollectionModal: boolean;
  showUploadInfoModal: boolean;
  uploadInfoOptions: UploadInfoDialogOptions;

  private selectedGroupSubscription: Subscription;
  private currentGroup: Group;
  currentCollection: ProjectCollection;

  reportTiming: TimerFn;

  isAnalysisDialogOpen = false;
  projectItemForAnalysisDialog: ProjectItem;
  collectionForAnalysisDialog: ProjectCollection;

  isSelectCollectionConfigurationDialogOpen = false;
  collectionForSelectCollectionConfigurationDialog: ProjectCollection;

  isCalculatedFieldConfigModalOpen = false;
  calculatedFieldCollection: Collection;

  isCollectionSettingsOpen = false;
  collectionSettingsCollection: Collection;
  collectionSettingsProject: Project;

  constructor(
    private authService: AuthService,
    private collectionService: CollectionService,
    private alertService: AlertService,
    public dialog: MatDialog,
    private dialogService: DialogService,
    private projectDetailService: ProjectDetailService,
    private analysisService: AnalysisService,
    private semantriaAccount: SemantriaAccount,
    public groupService: GroupService,
    private preferencesService: SemantriaPreferences,
    public configurationService: ConfigurationService,
    private storageAccountService: StorageAccountService,
    private googleAnalyticsService: AnalyticsService,
    private router: Router,
    private projectService: ProjectService,
    private predictiveService: PredictiveService,
    private downloadTaskService: DownloadTasksService,
    private systemService: SystemService,
    private iteractiveSmartSummaryService: InteractiveSmartSummaryService,
    private timeZoneService: TimezoneService
  ) {
    this.reportTiming = this.googleAnalyticsService.createTimer('Projects');
  }

  async ngOnInit() {
    this.selectedGroupSubscription = this.groupService.selectedGroup$.subscribe((group) => {
      this.currentGroup = group;
    });

    this.reportTiming('project_collections_loaded');
  }

  public onShowNewCollectionDialog(collection?: ProjectCollection) {
    this.projectDetailService.goToCollectionUpload(collection?.data);
  }

  private async loadConfigurations(): Promise<void> {
    const res = await this.projectDetailService.loadConfigurations();
    this.groupConfigurations = res.groupConfigurations;
    this.configurations = res.configurations;
  }

  public onAnalysisAction(action: AnalysisActionTypes, item: ProjectItem) {
    const collectionName = item.displayData.isDriverAnalysis
      ? item.calculation.collectionId
      : item.analysis.collectionName;
    const collection = this.projectCollections.find(
      (pc) => pc.data.collectionName === collectionName
    );

    if (!collection) {
      return;
    }

    switch (action) {
      case 'OPEN_RENAME_ANALYSIS_DIALOG':
        this.showNewAnalysisDialog(collection, item);
        break;
      case 'OPEN_DELETE_ANALYSIS_DIALOG':
        this.showDeleteAnalysisDialog(collection, item.analysis);
        break;
      case 'OPEN_NEW_ANALYSIS_DIALOG':
        this.showNewAnalysisDialog(collection, item);
        break;
      case 'OPEN_DELETE_DRIVER_ANALYSIS_DIALOG':
        this.showDeleteDriverAnalysisDialog(
          collection,
          collection.data.collectionName,
          this.project.id,
          item.calculation.id,
          item.calculation.displayName
        );
        break;
      case 'OPEN_RE_ANALYSIS_DIALOG':
        this.showReAanalyzeAnalysisDialog(collection, item.analysis);
        break;
      case 'OPEN_STOP_RE_ANALYSIS_DIALOG':
        this.showStopReAanalyzeAnalysisDialog(collection, item.analysis);
        break;
      case 'CREATE_EMBEDDINGS':
        this.onShowCreateEmbeddingsDialog(collection, item.analysis);
        break;
      case 'CHECK_EMBEDDINGS':
        this.onShowCheckEmbeddingsDialog(collection, item.analysis);
        break;
      case 'REMOVE_EMBEDDINGS':
        this.onShowRemoveEmbeddingsDialog(collection, item.analysis);
        break;
      case 'COPY_ID_TO_CLIPBOARD':
        this.systemService.copyToClipboard(item.analysis.name);
        this.alertService.showToast(translate('project-collection-actions.analysis-id-copied'));
    }
  }

  public async onShowCreateEmbeddingsDialog(collection: ProjectCollection, analysis: Analysis) {
    const translations = translateObject('project-collection-actions.generate-embeddings', {
      title: {
        name: analysis.displayName,
      },
      generating: {
        name: analysis.displayName,
      },
    });
    if (this.authService.isDebugUser) {
      const embeddingOptions = await this.iteractiveSmartSummaryService.getEmbeddingOptions();
      this.dialogService
        .showDialog<EmbeddingOptionsComponent, EmbeddingOptionsComponentOptions>(
          EmbeddingOptionsComponent,
          { embeddingOptions },
          {
            maxWidth: '60vw',
          },
          false
        )
        .afterClosed()
        .subscribe(async (result) => {
          if (result) {
            this.alertService.showToast(translations['generating']);
            try {
              await this.iteractiveSmartSummaryService.generateAnalysisEmbeddings(
                this.project.id,
                collection.data.collectionName,
                analysis.name,
                undefined,
                this.timeZoneService.timezone,
                result.model,
                result.strategy
              );
              this.googleAnalyticsService.sendEvent('generate_embeddings', 'Projects');
            } catch (error) {
              this.alertService.showErrorToast(translations['generate-failed'], error);
              this.googleAnalyticsService.sendEvent('generate_embeddings_failed', 'Projects');
            }
          }
        });
    } else {
      this.dialogService
        .showWarningPopup({
          title: translations['title'],
          content: translations['content'],
          confirmTitle: translations['confirm'],
          confirmColor: SimpleDialogButtonColor.red,
        })
        .afterClosed()
        .subscribe(async (result) => {
          if (result) {
            this.alertService.showToast(translations['generating']);
            try {
              await this.iteractiveSmartSummaryService.generateAnalysisEmbeddings(
                this.project.id,
                collection.data.collectionName,
                analysis.name,
                undefined,
                this.timeZoneService.timezone,
                undefined,
                undefined
              );
              this.googleAnalyticsService.sendEvent('generate_embeddings', 'Projects');
            } catch (error) {
              this.alertService.showErrorToast(translations['generate-failed'], error);
              this.googleAnalyticsService.sendEvent('generate_embeddings_failed', 'Projects');
            }
          }
        });
    }
  }

  public onShowRemoveEmbeddingsDialog(collection: ProjectCollection, analysis: Analysis) {
    const translations = translateObject('project-collection-actions.remove-embeddings', {
      title: {
        name: analysis.displayName,
      },
      generating: {
        name: analysis.displayName,
      },
      removing: {
        name: analysis.displayName,
      },
    });
    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.alertService.showToast(translations['removing']);
          try {
            await this.iteractiveSmartSummaryService.removeAnalysisEmbeddings(
              this.project.id,
              collection.data.collectionName,
              analysis.name
            );
            this.googleAnalyticsService.sendEvent('remove_embeddings', 'Projects');
          } catch (error) {
            this.alertService.showErrorToast(translations['remove-failed'], error);
            this.googleAnalyticsService.sendEvent('remove_embeddings_failed', 'Projects');
          }
        }
      });
  }

  public async onShowCheckEmbeddingsDialog(collection: ProjectCollection, analysis: Analysis) {
    let result: CheckEmbeddingsResult;
    try {
      result = await this.iteractiveSmartSummaryService.checkAnalysisEmbeddings(
        this.project.id,
        collection.data.collectionName,
        analysis.name,
        undefined,
        this.timeZoneService.timezone,
        analysis.embeddingModel,
        analysis.embeddingStrategy
      );
    } catch (error) {
      this.alertService.showErrorToast(
        translate('project-collection-actions.check-embedding.check-failed'),
        error
      );
    }

    const translations = translateObject('project-collection-actions.check-embeddings', {
      title: {
        analysisName: analysis.displayName,
      },
      content: {
        embeddings: result.embeddingCount,
        documents: result.analysisDocumentCount,
      },
    });

    this.dialogService.showWarningPopup({
      title: translations['title'],
      content: translations['content'],
    });
  }

  public async showNewAnalysisDialog(
    collection: ProjectCollection,
    projectItem?: ProjectItem,
    $event?
  ) {
    if ($event) {
      $event.detail.nativeEvent.stopImmediatePropagation();
    }

    //If there is a difference between the list of config permissions in the group and the current list of configs then
    //reload all the configs.
    const groupConfigs = [];
    const currentConfigs = [];
    if (this.currentGroup) {
      this.currentGroup.permissions.forEach((permission) => {
        if (permission.config_id && !permission.evaluation && !permission.removed) {
          groupConfigs.push(permission.config_id);
        }
      });
    }
    this.groupConfigurations.forEach((config) => {
      currentConfigs.push(config.id);
    });
    if (groupConfigs.sort().toString() !== currentConfigs.sort().toString()) {
      await this.loadConfigurations();
    }

    this.projectItemForAnalysisDialog = projectItem;
    this.collectionForAnalysisDialog = collection;

    this.isAnalysisDialogOpen = true;
  }

  async onCloseNewAnalysisDialog(result): Promise<void> {
    this.isAnalysisDialogOpen = false;

    if (!result) {
      return;
    }

    if (this.projectItemForAnalysisDialog?.analysis) {
      // update existing
      try {
        this.alertService.showToast(
          translate('project-collection-actions.new-analysis.updating', { name: result.name })
        );
        await this.analysisService.updateAnalysis({
          projectId: this.project.id,
          collectionName: this.projectItemForAnalysisDialog.analysis.collectionName,
          analysisId: this.projectItemForAnalysisDialog.analysis.name,
          notes: result.notes,
          displayName: result.name,
        });
        this.projectItemForAnalysisDialog.analysis.displayName = result.name;
        this.projectItemForAnalysisDialog.name = result.name;
        this.projectItemForAnalysisDialog.analysis.notes = result.notes;

        this.projectDetailService.changeSelectedItem(this.projectItemForAnalysisDialog);

        this.projectDetailService.onAnalysisEvent(
          'UPDATE',
          this.projectItemForAnalysisDialog.analysis
        );

        this.googleAnalyticsService.sendEvent('save_analysis', 'Projects');
      } catch (e) {
        let message = e.message || String(e);
        if (e.error && e.error.description) {
          message = e.error.description;
        }
        this.alertService.showErrorToast(
          translate('project-collection-actions.new-analysis.update-failed'),
          message
        );
        this.googleAnalyticsService.sendEvent('save_analysis_failed', 'Projects');
      }
    } else {
      // create new
      try {
        if (!result.isLanguageTemplate && !result.isIndustryPack && !result.isConfigurationRoute) {
          const processingConfig = await this.configurationService.getConfiguration(
            result.configurationId
          );
          if (processingConfig.callback_url || processingConfig.is_autoresponse_enabled) {
            this.alertService.showToast(
              translate('project-collection-actions.new-analysis.create-failed-config-changed', {
                name: processingConfig.name,
              })
            );
            return;
          }
        }

        this.alertService.showToast(
          translate('project-collection-actions.new-analysis.creating', { name: result.name })
        );
        await this.analysisService.createAnalysis({
          projectId: this.project.id,
          collectionName: this.collectionForAnalysisDialog.data.collectionName,
          analysisName: result.name,
          configId: result.configurationId,
          notes: result.notes,
        });
        this.preferencesService.savePreference('lastConfig', result.configurationId);
        this.googleAnalyticsService.sendEvent('create_analysis', 'Projects');
      } catch (e) {
        let message = e.message || String(e);
        let errorType = 'unknown';
        if (e.error && e.error.description) {
          message = e.error.description;
          errorType = e.error.type;
        }
        if (errorType === 'AnalysisInsufficientDocsBalance') {
          const translations = translateObject(
            'project-collection-actions.new-analysis.insufficient-balance'
          );
          this.dialogService.showSimpleDialog({
            title: translations['title'],
            content: translations['content'],
            extraContent: translations['description'],
            confirmTitle: translations['confirm'],
            confirmColor: SimpleDialogButtonColor.white,
            disableCancel: true,
          });
        } else {
          this.alertService.showErrorToast(
            translate('project-collection-actions.new-analysis.create-failed'),
            message
          );
        }
        this.googleAnalyticsService.sendEvent('create_analysis_failed', 'Projects');
      }
    }
    this.storageAccountService.groupFieldsSub.load(true);
  }

  completeUpload(pc: ProjectCollection, $event) {
    $event.detail.nativeEvent.stopImmediatePropagation();

    const upload = pc.uploads.find((u) => u.state === UploadState.INSPECTED);
    if (!upload) {
      return;
    }
    const uploadId = upload.id;
    this.router.navigate([
      `groups/${this.groupService.getSelectedGroupId(true)}/projects/${
        pc.data.projectId
      }/collections/${pc.data.collectionName}/uploads/${uploadId}/map`,
    ]);
  }

  public showUploadInfoDialog(collection: ProjectCollection) {
    const coll = this.projectCollections.find(
      (c) => c.data.collectionName === collection.data.collectionName
    );

    this.uploadInfoOptions = { collection: coll.data, upload: coll.upload };
    this.showUploadInfoModal = true;
  }

  public onUploadInfoModalClosed() {
    this.showUploadInfoModal = false;
  }

  public async onShowDriverAnalysisDialog(
    collection: ProjectCollection,
    widgetData: WidgetDriverAnalysisCreateOptions
  ) {
    if (collection.analyses.length) {
      if (!collection.driverAnalyses.length) {
        collection.driverAnalyses = (
          await this.predictiveService.listCalculations(
            collection.data.projectId,
            collection.data.collectionName,
            { pageSize: 1024 }
          )
        ).items;
      }
      this.showDriverAnalysisModal = true;
      this.driverAnalysisModalData = {
        collection,
        widgetData,
      };
    }
  }

  public onDriverAnalysisDialogClosed(result) {
    this.showDriverAnalysisModal = false;
    if (result) {
      const translations = translateObject('project-collection-actions.new-key-driver-analysis', {
        'create-success': {
          name: result.displayName,
        },
      });

      this.predictiveService
        .createDriverAnalysis(
          this.project.id,
          this.driverAnalysisModalData.collection.data.collectionName,
          result
        )
        .then((res: any) => {
          if (res.status === 'COMPLETE') {
            this.alertService.showToast(translations['create-success']);
            this.projectDetailService.projectRefreshNeeded();
            this.semantriaAccount.sub.load(true);
          } else if (res.status === 'PENDING') {
            this.alertService.showToast(
              translations['pending-success'],
              undefined,
              undefined,
              undefined,
              'open-download-center-pointer',
              'open-download-center'
            );
          } else {
            this.alertService.showErrorToast(
              `${translations['create-failed']}: ${res?.result?.message || translations['unknown-error']}`
            );
          }
        })
        .catch((err) =>
          this.alertService.showErrorToast(
            `${translations['create-failed']}: ${err.error?.description + ':' + err.error?.data}`
          )
        );
    }
  }

  public onShowCollectionDeleteDialog(collection: ProjectCollection) {
    const translations = translateObject('project-collection-actions.delete-collection', {
      title: {
        name: collection.data.displayName,
      },
      deleting: {
        name: collection.data.displayName,
      },
    });

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.alertService.showToast(translations['deleting']);
          try {
            await this.projectService.deleteCollection(
              this.project.id,
              collection.data.collectionName
            );
            this.projectDetailService.removeLastSelectedItem();
            this.projectDetailService.changeSelectedItem(undefined);
            this.projectDetailService.projectRefreshNeeded();
            this.googleAnalyticsService.sendEvent('delete_collection', 'Projects');
          } catch (error) {
            this.alertService.showErrorToast(translations['delete-failed'], error);
            this.googleAnalyticsService.sendEvent('delete_collection_failed', 'Projects');
          }
        }
      });
  }

  public showDeleteDriverAnalysisDialog(
    collection: ProjectCollection,
    collectionName: string,
    projectId: string,
    calculationId: string,
    displayName: string,
    cancelCallback?: (...args) => void
  ) {
    const translations = translateObject('project-collection-actions.delete-key-driver-analysis', {
      title: {
        name: displayName,
      },
      deleting: {
        name: displayName,
      },
    });

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        try {
          if (result) {
            this.projectDetailService.onDriverAnalysisEvent(
              'UPDATE',
              projectId,
              collectionName,
              calculationId,
              'DELETING'
            );

            this.alertService.showToast(translations['deleting']);
            await this.predictiveService.deleteCalculationById(
              projectId,
              collectionName,
              calculationId
            );
            this.projectDetailService.projectRefreshNeeded();
            this.googleAnalyticsService.sendEvent('delete_driver_analysis', 'Projects');
            this.projectDetailService.onDriverAnalysisEvent(
              'DELETE',
              projectId,
              collectionName,
              calculationId,
              'DELETED'
            );
          } else if (cancelCallback) {
            cancelCallback(collection, collectionName, projectId, calculationId, displayName);
          }
        } catch (error) {
          this.projectDetailService.onDriverAnalysisEvent(
            'DELETE',
            projectId,
            collectionName,
            calculationId,
            'ERROR'
          );
          this.alertService.showErrorToast(translations['delete-failed'], error);
          this.googleAnalyticsService.sendEvent('delete_driver_analysis_failed', 'Projects');
        }
      });
  }

  public showDeleteAnalysisDialog(
    collection: ProjectCollection,
    analysis: Analysis,
    cancelCallback?: (...args) => void
  ) {
    if (analysis.status === 'OPEN' || analysis.status === 'DELETING') {
      return;
    }

    const translations = translateObject('project-collection-actions.delete-analysis', {
      title: {
        name: analysis.displayName,
      },
      deleting: {
        name: analysis.displayName,
      },
    });
    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        try {
          if (result) {
            this.alertService.showToast(translations['deleting']);
            await this.analysisService.deleteAnalysis(
              collection.data.collectionName,
              analysis.name,
              this.project.id
            );
            analysis.status = 'DELETING';
            this.projectDetailService.onAnalysisEvent('DELETE', analysis);
            this.googleAnalyticsService.sendEvent('delete_analysis', 'Projects');
          } else if (cancelCallback) {
            cancelCallback(collection, analysis);
          }
        } catch (error) {
          this.alertService.showErrorToast(translations['delete-failed'], error);
          this.googleAnalyticsService.sendEvent('delete_analysis_failed', 'Projects');
        }
      });
  }

  //Need to notify table
  public onToggleLockCollection(collection: Collection) {
    const isLocked = collection.locked;
    if (isLocked) {
      this.collectionService
        .unlockCollection(collection.projectId, collection.collectionName)
        .then(() =>
          this.alertService.showToast(
            translate('project-collection-actions.collection-unlock-success', {
              name: collection.displayName,
            })
          )
        )
        .catch(() =>
          this.alertService.showToast(
            translate('project-collection-actions.collection-unlock-failed', {
              name: collection.displayName,
            })
          )
        );
    } else {
      this.collectionService
        .lockCollection(collection.projectId, collection.collectionName)
        .then(() =>
          this.alertService.showToast(
            translate('project-collection-actions.collection-lock-success', {
              name: collection.displayName,
            })
          )
        )
        .catch(() =>
          this.alertService.showToast(
            translate('project-collection-actions.collection-lock-failed', {
              name: collection.displayName,
            })
          )
        );
    }
  }

  public onAutoAnalysisButtonClick(collectionItem: ProjectItem) {
    switch (collectionItem.displayData.autoAnalysisButtonData.autoAnalysisAction) {
      case AutoAnalysisAction.ShowAutoModal:
        this.collectionForSelectCollectionConfigurationDialog = collectionItem.collection;
        this.isSelectCollectionConfigurationDialogOpen = true;
        break;

      case AutoAnalysisAction.TurnOffAuto:
        this.saveCollection(
          collectionItem.collection,
          false,
          undefined,
          translate('project-collection-actions.auto-analysis-off')
        );
        break;

      case AutoAnalysisAction.TurnOnAuto:
        this.saveCollection(
          collectionItem.collection,
          true,
          collectionItem.collection.analyses[0].configId,
          translate('project-collection-actions.auto-analysis-on')
        );
        break;
    }
  }

  onCloseSelectCollectionConfigurationDialog(selectedConfigId?: string): void {
    this.isSelectCollectionConfigurationDialogOpen = false;
    if (!selectedConfigId) {
      this.collectionForSelectCollectionConfigurationDialog.data.isAutoAnalyze = false;
      return;
    }

    this.saveCollection(
      this.collectionForSelectCollectionConfigurationDialog,
      true,
      selectedConfigId
    );
  }

  private saveCollection(
    collection: ProjectCollection,
    autoAnalyze: boolean,
    selectedConfigId?: string,
    toastSuccessMessage?: string
  ) {
    collection.data.isAutoAnalyze = autoAnalyze;
    collection.data.configId = selectedConfigId ?? collection.data.configId;
    const successToast =
      toastSuccessMessage ?? translate('project-collection-actions.collection-update-success');

    this.collectionService
      .updateCollection(collection.data)
      .then(() => {
        this.alertService.showToast(successToast);
        this.projectDetailService.projectRefreshNeeded();
      })
      .catch((err) => {
        let message = err.message || err;
        if (err.error && err.error.description) {
          message = err.error.description;
        }

        this.alertService.showErrorToast(
          translate('project-collection-actions.collection-update-failed'),
          message
        );
      });
  }

  public async showReAanalyzeAnalysisDialog(
    collection: ProjectCollection,
    analysis: Analysis,
    $event?
  ) {
    if (analysis.status === 'OPEN' || analysis.status === 'DELETING') {
      return;
    }
    if ($event) {
      $event.detail.nativeEvent.stopImmediatePropagation();
    }

    //If there is a difference between the list of config permissions in the group and the current list of configs then
    //reload all the configs.
    const groupConfigs = [];
    const currentConfigs = [];
    if (this.currentGroup) {
      this.currentGroup.permissions.forEach((permission) => {
        if (permission.config_id && !permission.evaluation && !permission.removed) {
          groupConfigs.push(permission.config_id);
        }
      });
    }
    this.groupConfigurations.forEach((config) => {
      currentConfigs.push(config.id);
    });
    if (groupConfigs.sort().toString() !== currentConfigs.sort().toString()) {
      await this.loadConfigurations();
    }

    this.dialogService
      .showDialog(
        ReAnalyzeModalComponent,
        {
          groupConfigurations: this.groupConfigurations,
          collection,
          projectId: this.project.id,
          analysis,
          groupId: this.currentGroup.group_id,
        },
        { width: '800px' },
        false
      )
      .afterClosed()
      .subscribe(async (result) => {
        try {
          if (result) {
            if (
              !result.isLanguageTemplate &&
              !result.isIndustryPack &&
              !result.isConfigurationRoute
            ) {
              const processingConfig = await this.configurationService.getConfiguration(
                result.configurationId
              );
              if (processingConfig.callback_url || processingConfig.is_autoresponse_enabled) {
                this.alertService.showToast(
                  translate(
                    'project-collection-actions.reanalyze.reanalyze-failed-config-changed',
                    { name: processingConfig.name }
                  )
                );
                return;
              }
            }

            this.alertService.showToast(
              translate('project-collection-actions.reanalyze.reanalyzing', {
                name: analysis.displayName,
              })
            );
            await this.analysisService.reAnalyzeAnalysis(
              collection.data.collectionName,
              analysis.name,
              this.project.id,
              result.configurationId
            );
            analysis.status = 'QUEUED';
            this.preferencesService.savePreference('lastConfig', result.configurationId);
            this.googleAnalyticsService.sendEvent('re_analyze_analysis', 'Projects');
          }
        } catch (e) {
          let errorType = 'unknown';
          if (e.error && e.error.description) {
            errorType = e.error.type;
          }
          if (errorType === 'AnalysisInsufficientDocsBalance') {
            const translations = translateObject(
              'project-collection-actions.new-analysis.insufficient-balance'
            );
            this.dialogService.showSimpleDialog({
              title: translations['title'],
              content: translations['content'],
              extraContent: translations['description'],
              confirmTitle: translations['confirm'],
              confirmColor: SimpleDialogButtonColor.white,
              disableCancel: true,
            });
          } else {
            this.alertService.showErrorToast(
              translate('project-collection-actions.reanalyze.reanalyze-failed', {
                name: analysis.displayName,
              })
            );
          }
          this.googleAnalyticsService.sendEvent('re_analyze_analysis_failed', 'Projects');
        } finally {
          this.projectDetailService.projectRefreshNeeded();
        }
      });
  }

  public showStopReAanalyzeAnalysisDialog(collection: ProjectCollection, analysis: Analysis) {
    const translations = translateObject('project-collection-actions.stop-reanalyze', {
      title: {
        name: analysis.displayName,
      },
      'stop-success': {
        name: analysis.displayName,
      },
    });
    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        try {
          if (result) {
            this.alertService.showToast(translations['stop-success']);
            await this.analysisService.stopReAnalyzeAnalysis(
              collection.data.collectionName,
              analysis.name,
              this.project.id
            );
            analysis.status = 'IDLE';
            this.googleAnalyticsService.sendEvent('stop_re_analyze_analysis', 'Projects');
          }
        } catch (error) {
          this.alertService.showErrorToast(translations['stop-failed'], error);
          this.googleAnalyticsService.sendEvent('stop_re_analyze_analysis_failed', 'Projects');
        }
      });
  }

  onShowRenameCollectionDialog(collection: ProjectCollection) {
    this.currentCollection = collection;
    this.showRenameCollectionModal = true;
  }

  async onRenameCollectionDialogClosed(collectionData: Collection) {
    this.showRenameCollectionModal = false;
    if (collectionData) {
      try {
        await this.collectionService.updateCollection(collectionData);
        this.alertService.showToast(
          translate('project-collection-actions.rename-collection.collection-name-saved')
        );
      } catch (e) {
        this.alertService.showErrorToast(
          translate(
            'project-collection-actions.rename-collection.failed-to-change-collection-name'
          ),
          e
        );
      }
    }
  }

  public downloadAnalysisExcelReport(analysis: Analysis, reportType: TaskType, fileNameSuffix) {
    const groupId = this.groupService.getSelectedGroupId(true);

    this.downloadTaskService
      .createTask({
        _type: reportType,
        groupId,
        format: 'EXCEL',
        projectId: this.project.id,
        filename: `${analysis.displayName}-${fileNameSuffix}`,
        source: 'Collection',
        size: 0,
        query: `(analyses.analysisName: "${analysis.name}" IN analyses)`,
        sortField: 'createdAt',
        sortOrder: 'DESC',
        collectionId: analysis.collectionName,
        analysisId: analysis.name,
      })
      .then((_result) => {
        this.alertService.showToast(
          translate(
            'project-collection-actions.download-analysis-report.report-is-being-generated'
          ),
          undefined,
          undefined,
          undefined,
          'open-download-center-pointer',
          'open-download-center'
        );
      })
      .catch((error) => {
        this.alertService.showErrorToast(
          translate('project-collection-actions.download-analysis-report.failed-to-create'),
          error
        );
      });
  }

  ngOnDestroy() {
    this.selectedGroupSubscription?.unsubscribe();
  }

  onShowSceneSelectDialog(collection: ProjectCollection) {
    this.sceneSelectModalData = {
      collection,
    };
    this.showSceneSelectModal = true;
  }

  onSceneSelectDialogClosed($event) {
    if ($event) {
      this.sceneSelectModalData.collection.data.summarySceneId = $event.summarySceneId;
    }

    this.showSceneSelectModal = false;
  }

  onCopyCollectionIdToClipboard(collection: ProjectCollection) {
    this.systemService.copyToClipboard(collection.data.collectionName);
    this.alertService.showToast(translate('project-collection-actions.collection-id-copied'));
  }

  onOpenCalculatedFieldConfigModal(collection: Collection): void {
    this.calculatedFieldCollection = collection;

    this.isCalculatedFieldConfigModalOpen = true;
  }

  onCloseCalculatedFieldConfigModal(): void {
    this.isCalculatedFieldConfigModalOpen = false;
  }

  onOpenCollectionSettings(collection: Collection, project: Project): void {
    this.collectionSettingsCollection = collection;
    this.collectionSettingsProject = project;

    this.isCollectionSettingsOpen = true;
  }

  onCloseCollectionSettings(): void {
    this.isCollectionSettingsOpen = false;
  }
}
